import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, LinearProgress, FormControl, FormControlLabel, RadioGroup, Radio, Chip, TextField, InputLabel, Select, MenuItem } from "@material-ui/core";
import { Booking } from "api/bookings";
import { useSnackbar } from "notistack";
import { Autocomplete } from "@material-ui/lab";
import { getRootBookingGroups, getRootGroups, patchRootBookingGroups, RootGroup } from "api/root/groups";
import i18n from "../i18n";

// prop types
interface EditRootGroupsDialogProps {
    booking?: Booking,
    show: boolean,
    onClose: () => void,
}

// edit root groups dialog
const EditRootGroupsDialog = ({ booking, show, onClose }: EditRootGroupsDialogProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.associated?.message ? error.associated.message : `${error.description} (${error.type})`}`,
        { variant: "error" },
    );

    // ui state
    const [applyToFutureInSeries, setApplyToFutureInSeries] = useState("false");
    const [availableRootGroups, setAvailableRootGroups] = useState<RootGroup[]>([]);
    const [currentRootGroups, setCurrentRootGroups] = useState<RootGroup[]>([]);
    const [invitedRootGroups, setInvitedRootGroups] = useState<RootGroup[]>([]);
    const [isLoading, setLoading] = useState(false);
    const [query, setQuery] = useState("");
    const [removedRootGroups, setRemovedRootGroups] = useState<string[]>([]);
    const [showEditRepeatedBookingDialog, setShowEditRepeatedBookingDialog] = useState(false);
    const [daysBeforeStartDate, setDaysBeforeStartDate] = useState<number[]>([]);
    const [selectDayForInvite, setSelectDayForInvite] = useState<number>(1);
    const [showNotifyDayDialog, setShowNotifyDayDialog] = useState<boolean>(false);
    const [lastSelectedGroupId, setLastSelectedGroupId] = useState<string>("");

    // fetch root groups
    const fetchRootGroups = async () => {
        setAvailableRootGroups((await getRootGroups({ limit: 10, query })).data);
    };
    useEffect(() => { fetchRootGroups(); }, [query]); // eslint-disable-line

    // fetch current booking root groups
    const fetchCurrentRootGroups = async () => {
        if (booking) {
            setCurrentRootGroups((await getRootBookingGroups({ bookingId: booking.id })).data);
            setInvitedRootGroups([]);
            setRemovedRootGroups([]);
            const startDate = new Date(booking?.startDate);
            const currentTime = new Date().getTime();
            const startTime = startDate.getTime();
            const diffDays = (startTime - currentTime) / (1000 * 3600 * 24);
            setDaysBeforeStartDate(range(1, Math.round(diffDays)));
        }
    };
    useEffect(() => { fetchCurrentRootGroups(); }, [booking]); // eslint-disable-line

    const range = (start: number, stop: number) => Array.from({ length: stop - start + 1 }, (_, i) => start + i);

    const updateInvitedRootGroups = (day: number, invitedGroup?: RootGroup[] | undefined, groupId?: string | undefined) => {
        setSelectDayForInvite(day);
        const data = invitedGroup || invitedRootGroups;
        const lastGroupId = groupId || lastSelectedGroupId;
        const updatedData: RootGroup[] = data?.map((item) => {
            if (item?.id === lastGroupId) {
                return {
                    ...item,
                    inviteDay: day,
                };
            }
            return item;
        });
        setInvitedRootGroups(updatedData);
    };

    // api request
    const saveAction = async () => {
        setShowEditRepeatedBookingDialog(false);
        setLoading(true);
        const addGroups = invitedRootGroups?.map((g) => ({ groupId: g.id, notifyDaysBeforeStartDate: g?.inviteDay || 1 }));
        const params = {
            addGroups,
            applyToFutureInSeries: (booking?.repeatType !== "never") ? (applyToFutureInSeries === "true") : undefined,
            bookingId: booking!.id,
            removeGroupIds: removedRootGroups,
        };
        try {
            await patchRootBookingGroups(params);
            fetchCurrentRootGroups();
            onClose();
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // render
    return (
        <>
            <Dialog
                fullWidth
                open={show}
                onClose={onClose}
            >
                <DialogTitle>{i18n.editRootGroups}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>

                        {/* invite new groups */}
                        <Grid item xs={12}>
                            <FormControl fullWidth variant="outlined">
                                <Autocomplete
                                    filterSelectedOptions
                                    getOptionLabel={(option) => `${option.groupName} (${option.numMembers})`}
                                    getOptionSelected={(option, value) => option.id === value.id}
                                    multiple
                                    options={invitedRootGroups.concat(availableRootGroups).filter((g) => !currentRootGroups.map((h) => h.id).includes(g.id))}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={i18n.inviteNewGroups}
                                            variant="outlined"
                                        />
                                    )}
                                    renderTags={(value, getTagProps) => value.map((option, index) => {
                                        const beforeInviteDay = `${daysBeforeStartDate?.length > 0 && option?.inviteDay ? `${option.inviteDay} ${i18n.days} | ` : ""}`;
                                        return (
                                            <Chip
                                                clickable
                                                onClick={() => {
                                                    setLastSelectedGroupId(option?.id);
                                                    setShowNotifyDayDialog(true);
                                                    setSelectDayForInvite(option?.inviteDay || 1);
                                                }}
                                                label={`${beforeInviteDay}${option.groupName} (${option.numMembers})`}
                                                {...getTagProps({ index })}
                                            />
                                        );
                                    })}
                                    value={invitedRootGroups}
                                    onChange={(e, v, r, d) => {
                                        setInvitedRootGroups(v);
                                        if (r === "select-option") {
                                            const groupId = d?.option?.id || "";
                                            setLastSelectedGroupId(groupId);
                                            if (daysBeforeStartDate?.length > 0) {
                                                setShowNotifyDayDialog(true);
                                                updateInvitedRootGroups(1, v, groupId);
                                            }
                                        }
                                    }}
                                    onInputChange={(e, v, r) => (r === "input") && setQuery(v)}
                                />
                            </FormControl>
                        </Grid>

                        {/* remove groups */}
                        <Grid item xs={12}>
                            <FormControl fullWidth variant="outlined">
                                <Autocomplete
                                    clearText={i18n.reset}
                                    forcePopupIcon={false}
                                    multiple
                                    open={false}
                                    disableClearable
                                    options={currentRootGroups || []}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            InputProps={{ ...params.InputProps, readOnly: true }}
                                            label={i18n.removeInvitedGroups}
                                            variant="outlined"
                                        />
                                    )}
                                    renderTags={(value, getTagProps) => value.map((option, index) => {
                                        const inviteDay = `${option?.notifyDaysBeforeStartDate ? `${option.notifyDaysBeforeStartDate} ${i18n.days} | ` : ""}`;
                                        return (
                                            <Chip
                                                label={`${inviteDay}${option.groupName} (${option.numMembers})`}
                                                {...getTagProps({ index })}
                                                onDelete={() => setRemovedRootGroups([...removedRootGroups, option.id])}
                                            />
                                        );
                                    })}
                                    value={currentRootGroups.filter((g) => !removedRootGroups.includes(g.id)) || []}
                                    onInputChange={(e, v, r) => (r === "clear") && setRemovedRootGroups([])}
                                />
                            </FormControl>
                        </Grid>

                    </Grid>
                </DialogContent>
                {isLoading && <LinearProgress />}
                <DialogActions>
                    <Button color="secondary" onClick={onClose}>
                        {i18n.cancel}
                    </Button>
                    <Button color="primary" disabled={isLoading} onClick={(booking?.repeatType !== "never") ? () => setShowEditRepeatedBookingDialog(true) : saveAction}>
                        {i18n.save}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* edit repeated booking dialog */}
            <Dialog
                open={showEditRepeatedBookingDialog}
                onClose={() => setShowEditRepeatedBookingDialog(false)}
            >
                <DialogTitle>{i18n.editRepeatedBooking}</DialogTitle>
                <DialogContent dividers>
                    <RadioGroup value={applyToFutureInSeries} onChange={(e) => setApplyToFutureInSeries(e.target.value)}>
                        <FormControlLabel value="false" control={<Radio color="primary" />} label={i18n.thisBooking} />
                        <FormControlLabel value="true" control={<Radio color="primary" />} label={i18n.thisBookingAndFollowing} />
                    </RadioGroup>
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={() => setShowEditRepeatedBookingDialog(false)}>
                        {i18n.cancel}
                    </Button>
                    <Button color="primary" onClick={saveAction}>
                        {i18n.ok}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* notify days before StartDate */}
            <Dialog
                open={showNotifyDayDialog}
                onClose={() => setShowNotifyDayDialog(false)}
            >
                <DialogTitle>{i18n.editNotifyNumberOfDays}</DialogTitle>
                <DialogContent dividers>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>{i18n.days}</InputLabel>
                        <Select
                            label={i18n.days}
                            value={selectDayForInvite}
                            onChange={(e) => updateInvitedRootGroups(e.target.value as number)}
                        >
                            {daysBeforeStartDate.map((t) => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default EditRootGroupsDialog;
